import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api/axios';
import './LeadPlannerDecision.css';

const CRM_LABELS = {
  CRM_DO_NOT_CONTACT: 'Contact geblokkeerd',
  CRM_OPEN_OPPORTUNITY_REVIEW: 'Open opportunity',
  CRM_EXISTING_CUSTOMER_REVIEW: 'Bestaande klant',
  CRM_RECENT_ACTIVITY_COOLDOWN: 'Recente CRM-activiteit',
};

const ACTION_LABELS = {
  stop_outreach: 'Outreach stoppen',
  manual_review: 'Handmatige review',
  wait: 'Wachten',
  draft_connection_request: 'LinkedIn-connectieverzoek opstellen',
  draft_linkedin_message: 'LinkedIn-bericht opstellen',
  draft_email: 'E-mail opstellen',
};

const MODE_LABELS = {
  manual: 'Manual',
  hybrid: 'Hybrid',
  agent: 'AI Agent',
};

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString('nl-NL');
}

function ModeNotice({ mode, planningAllowed, handoffActive }) {
  if (mode === 'agent') {
    return <div className="planner-mode-notice agent"><strong>AI Agent</strong><span>Deze lead valt volledig binnen de campagnebrede Agent Mode.</span></div>;
  }
  if (mode === 'hybrid' && handoffActive) {
    return <div className="planner-mode-notice hybrid active"><strong>Hybrid · overgedragen</strong><span>De handmatige flow heeft deze lead aan de AI Agent overgedragen. De Planner mag vanaf hier dynamisch handelen.</span></div>;
  }
  if (mode === 'hybrid') {
    return <div className="planner-mode-notice hybrid"><strong>Hybrid · handmatige fase</strong><span>Deze lead heeft de Hand over to Agent-stap nog niet bereikt. AI-planning blijft geblokkeerd.</span></div>;
  }
  return <div className="planner-mode-notice manual"><strong>Manual</strong><span>De vaste workflow is leidend. De AI Planner is voor deze lead niet bevoegd.</span></div>;
}

export default function LeadPlannerDecision({ campaignId, leadId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/agent/campaigns/${campaignId}/leads/${leadId}/plan/latest`);
      setData(response.data || null);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    }
  }, [campaignId, leadId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <section className="lead-agent-card planner-decision-card"><h2>Plannerbeslissing</h2><p className="planner-decision-error">{error}</p></section>;
  if (!data) return null;

  const plan = data.plan;
  const crm = data.crmContext || {};
  const policy = plan?.crmPolicy || {};
  const retryAt = formatDate(policy.retryAt || plan?.followUpAt);
  const owner = crm.primaryOwner || null;
  const mode = data.mode || 'manual';

  return <section className="lead-agent-card planner-decision-card">
    <div className="lead-agent-section-header">
      <div><h2>Plannerbeslissing</h2><p>Waarom de agent nu wel, niet of later contact plant.</p></div>
      <div className="planner-decision-badges">
        <span className={`planner-mode-badge mode-${mode}`}>{MODE_LABELS[mode] || mode}</span>
        {policy.blocked && <span className={`planner-crm-badge ${policy.hardStop ? 'blocked' : policy.reviewRequired ? 'review' : 'wait'}`}>CRM · {CRM_LABELS[policy.code] || 'policy'}</span>}
      </div>
    </div>

    <ModeNotice mode={mode} planningAllowed={data.planningAllowed === true} handoffActive={data.workflowHandoffActive === true} />

    {plan ? <>
      <div className="planner-decision-main">
        <div><span>Volgende actie</span><strong>{ACTION_LABELS[plan.action] || plan.action || 'Geen actie'}</strong></div>
        <div><span>Kanaal</span><strong>{plan.channel === 'linkedin' ? 'LinkedIn' : plan.channel === 'email' ? 'E-mail' : 'Geen kanaal'}</strong></div>
        <div><span>Subdoel</span><strong>{plan.subgoal || '—'}</strong></div>
        <div><span>Volgende planning</span><strong>{formatDate(plan.nextPlanningAt) || '—'}</strong></div>
      </div>
      <p className="planner-decision-reason">{plan.reason || 'Geen aanvullende reden opgeslagen.'}</p>
      {retryAt && policy.code === 'CRM_RECENT_ACTIVITY_COOLDOWN' && <p className="planner-decision-note">CRM-cooldown tot <strong>{retryAt}</strong>.</p>}
    </> : <p className="planner-decision-empty">{data.planningAllowed === false ? 'Er is nog geen AI-plan omdat de Planner voor deze lead niet bevoegd is.' : 'Deze lead is nog niet door de planner beoordeeld.'}</p>}

    {crm.linked && <div className="planner-crm-context">
      <span>CRM: {(crm.providers || []).join(', ') || 'gekoppeld'}</span>
      {crm.lifecycleStage && <span>Lifecycle: {crm.lifecycleStage}</span>}
      {crm.leadStatus && <span>Status: {crm.leadStatus}</span>}
      {crm.openOpportunity === true && <span>Open opportunity</span>}
      {crm.doNotContact === true && <span>Do-not-contact</span>}
      {owner?.name && <span>CRM-owner: {owner.name}{owner.ownerType === 'team' ? ' (team)' : ''}</span>}
      {!owner?.name && crm.ownerId && <span>CRM-owner gekoppeld</span>}
      {owner?.stale && <span>Owner-info uit cache</span>}
      {crm.lastCrmActivityAt && <span>Laatste CRM-activiteit: {formatDate(crm.lastCrmActivityAt)}</span>}
    </div>}
  </section>;
}
